import { type ReactNode } from "react";

/**
 * MaintenanceGuard - Wrapper component for maintenance mode protection
 * 
 * When maintenance mode is active, this displays a read-only overlay.
 * Currently passes through children directly - maintenance mode can be
 * triggered by setting a flag in localStorage or a backend API.
 * 
 * To enable maintenance mode manually (for testing):
 * localStorage.setItem('staff_maintenance_mode', 'true')
 * 
 * To disable:
 * localStorage.removeItem('staff_maintenance_mode')
 */
export function MaintenanceGuard({ children }: { children: ReactNode }) {
  // Simple localStorage-based maintenance mode check
  // This can be replaced with a backend API call when available
  const isMaintenanceMode = typeof window !== 'undefined' 
    && localStorage.getItem('staff_maintenance_mode') === 'true';

  if (isMaintenanceMode) {
    return (
      <div className="relative">
        {children}
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-background border rounded-lg p-6 max-w-md mx-4 text-center">
            <h2 className="text-xl font-semibold mb-2">Maintenance Mode</h2>
            <p className="text-muted-foreground">
              The system is currently in read-only mode. Some features may be temporarily unavailable.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
